/**
 * HTTP 客户端工厂
 *
 * 为各 DDD 模块提供统一的 axios 实例创建逻辑：
 *  - 自动注入 Authorization Bearer Token
 *  - 统一的错误状态码处理与 ElMessage 提示
 *  - 支持 silent 配置跳过错误弹窗
 *
 * @module src/shared/http
 */

import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";
import {
  API_BASE_URL,
  API_TIMEOUT,
  LLM_TIMEOUT,
  LLM_ENDPOINT_PATTERNS,
} from "@/config/apiConfig";
import {
  extractErrorBodyMessage,
  extractReportTraceId,
  formatReportTag,
} from "./apiError";
import { attachAccessToken, tryRecoverSession } from "./sessionRecovery";
import { getEntitlementDenialMessage } from "../capability/entitlementDenial";

// 并发请求同时 403 时，同一 URL 短窗口内只弹一次，避免连串「没有访问权限」
const FORBIDDEN_DEDUPE_WINDOW_MS = 3000;
const recentForbiddenAt = new Map<string, number>();

export interface HttpRequestConfig extends AxiosRequestConfig {
  silent?: boolean;
  _skipGlobalError?: boolean;
  /** 内部字段：幂等 GET 瞬时失败的已重试次数 */
  _transientRetryCount?: number;
  /** 特殊调用可显式关闭 GET 瞬时重试 */
  _skipTransientRetry?: boolean;
  /**
   * 跳过 401 会话恢复链（配合 silent 使用：由调用方自行展示错误文案）。
   * 典型场景：自助改密时「当前密码错误」返回 401，不应触发 refresh/重放/登出跳转。
   */
  _skipAuthRecovery?: boolean;
}

export interface HttpClient
  extends Omit<AxiosInstance, "get" | "post" | "put" | "patch" | "delete"> {
  get<T = unknown>(url: string, config?: HttpRequestConfig): Promise<T>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T>;
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T>;
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T>;
  delete<T = unknown>(url: string, config?: HttpRequestConfig): Promise<T>;
}

function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error) && error.response) {
    const status = error.response.status;
    const detailMessage = extractErrorBodyMessage(error.response.data);

    switch (status) {
      case 400:
        return detailMessage || "请求参数错误";
      case 401:
        // 维持固定文案：401 有独立的登录跳转逻辑，不受后端文案影响
        return "未授权，请重新登录";
      case 403:
        return "没有访问权限";
      case 404:
        return detailMessage || "请求的资源不存在";
      case 500:
        // 后端统一响应体的 error_message/details 透传（曾硬编码「服务器内部错误」，
        // 连生产脱敏后的安全文案也一并丢失）；无结构化消息时才退回默认文案
        return detailMessage || "服务器内部错误";
      default:
        return detailMessage || `请求失败 (${status})`;
    }
  }
  if (axios.isAxiosError(error) && error.request)
    return "网络连接失败，请检查网络";
  return error instanceof Error ? error.message : "未知错误";
}

const TRANSIENT_GET_STATUS = new Set([500, 502, 503, 504]);
const TRANSIENT_GET_RETRY_DELAY_MS = 300;

function shouldRetryTransientGet(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.config) return false;
  const config = error.config as HttpRequestConfig;
  if (config._skipTransientRetry) return false;
  if ((config.method || "get").toLowerCase() !== "get") return false;
  if ((config._transientRetryCount || 0) >= 1) return false;
  return !error.response || TRANSIENT_GET_STATUS.has(error.response.status);
}

/**
 * 创建预配置的 axios 实例
 *
 * 响应拦截器会自动解包 `response.data`，调用方直接得到业务数据。
 */
export function createHttpClient(): HttpClient {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use(
    (config) => {
      // LLM 密集型端点自动使用更长超时
      const url = config.url || "";
      if (LLM_ENDPOINT_PATTERNS.some((p) => url.includes(p))) {
        config.timeout = LLM_TIMEOUT;
      }
      return attachAccessToken(config);
    },
    (error) => {
      console.error("请求配置错误:", error);
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => response.data,
    async (error) => {
      const recovered = await tryRecoverSession(instance, error);
      if (recovered) {
        return recovered;
      }

      // GET 是幂等读取；公网数据库或网关瞬时抖动时静默重试一次。
      // 写请求绝不自动重试，避免重复创建、保存或扣费。
      if (shouldRetryTransientGet(error)) {
        const retryConfig = error.config as HttpRequestConfig;
        retryConfig._transientRetryCount =
          (retryConfig._transientRetryCount || 0) + 1;
        await new Promise((resolve) =>
          setTimeout(resolve, TRANSIENT_GET_RETRY_DELAY_MS),
        );
        return instance.request(retryConfig);
      }

      const message = handleApiError(error);
      // 支持 _skipGlobalError 配置
      const skipGlobalError =
        (error.config as any)?._skipGlobalError ||
        (error.config as any)?.silent;
      if (error.response?.status !== 401 && !skipGlobalError) {
        const status = error.response?.status;
        const urlKey = String(error.config?.url || "");
        const now = Date.now();
        const lastForbiddenAt = recentForbiddenAt.get(urlKey) ?? 0;
        const shouldNotify =
          status !== 403 ||
          now - lastForbiddenAt >= FORBIDDEN_DEDUPE_WINDOW_MS;
        if (status === 403 && shouldNotify) {
          recentForbiddenAt.set(urlKey, now);
        }
        // 模块未授权不是异常：普通提醒条 + 业务名称文案（不出现能力代码/状态码）
        const denialMessage = getEntitlementDenialMessage(error);
        if (shouldNotify) {
          if (denialMessage) {
            ElMessage.info(denialMessage);
          } else {
            // 报障编号：追踪号前 8 位弱化展示，凭此号可反查服务端日志；
            // 完整上下文（url/status/trace_id/error_code）进 console.error 供复制
            const traceId = extractReportTraceId(error);
            ElMessage.error(`${message}${formatReportTag(traceId)}`);
          }
        }
        // 去重只抑制 UI 弹窗；每次 403/错误仍保留完整排障上下文
        const errorBody = error.response?.data as
          | { error_code?: unknown }
          | undefined;
        console.error("[API错误]", {
          url: error.config?.url,
          status: error.response?.status,
          trace_id: extractReportTraceId(error),
          error_code: errorBody?.error_code,
        });
      }
      return Promise.reject(error);
    },
  );

  return instance as HttpClient;
}

/** 共享 HTTP 实例（大多数模块可直接使用此实例） */
const httpClient = createHttpClient();

export async function get<T>(
  url: string,
  config?: HttpRequestConfig,
): Promise<T> {
  return httpClient.get(url, config);
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: HttpRequestConfig,
): Promise<T> {
  return httpClient.post(url, data, config);
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: HttpRequestConfig,
): Promise<T> {
  return httpClient.put(url, data, config);
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: HttpRequestConfig,
): Promise<T> {
  return httpClient.patch(url, data, config);
}

export async function del<T>(
  url: string,
  config?: HttpRequestConfig,
): Promise<T> {
  return httpClient.delete(url, config);
}

export default httpClient;
